/**
 * Does this organization provide leads to its agents? Yes, no, or unanswered.
 *
 * The story used to carry one boolean, `providesLeads`, defaulting to `false`
 * in both the editor and the onboarding wizard. The public story rendered that
 * `false` as the affirmative chip "Self-generated" — so an organization that
 * had never been asked the question appeared to answer it, and appeared to
 * tell agents they would be prospecting for themselves (founder audit,
 * 2026-09-17). Empty data must never become a claim.
 *
 * So the authoritative value is now the tri-state `leadGenerationAnswer`, and
 * absent means absent. The legacy boolean is kept in sync on every write
 * because `provides_leads` is a mirrored column that the fit score, Compare and
 * the draft-profile Edge Function read. It is three-state too (OD-1, Joe
 * 2026-10-29): true is "provided", false is "self", and null is unanswered,
 * which the fit score leaves out rather than scoring as "no".
 *
 * Reading legacy rows, the asymmetry is deliberate and is the whole point:
 *
 * - `providesLeads === true` is a real answer. Someone moved that switch on;
 *   nothing else sets it. It resolves to "provided".
 * - `providesLeads === false`, or missing, is indistinguishable from the
 *   default nobody touched. It resolves to unanswered, never to "self".
 *
 * An organization that genuinely wants to say "our agents generate their own"
 * says so by choosing it, which writes `leadGenerationAnswer: "self"`.
 */

export type LeadGenerationAnswer = "provided" | "self";

/** The shape both the editor form and the stored document share. */
export interface LeadGenerationFields {
  leadGenerationAnswer?: LeadGenerationAnswer | "" | null;
  providesLeads?: boolean | null;
}

/**
 * The organization's answer, or `null` when it has not given one.
 *
 * `null` must render as nothing at all — not as a "No", not as a neutral chip,
 * and not as a prompt aimed at the agent reading the story.
 */
export function resolveLeadGenerationAnswer(
  profileData: LeadGenerationFields | null | undefined,
): LeadGenerationAnswer | null {
  const stored = profileData?.leadGenerationAnswer;
  if (stored === "provided" || stored === "self") return stored;
  // Legacy: only an explicit true carries intent. See the header.
  return profileData?.providesLeads === true ? "provided" : null;
}

/**
 * The patch that records an answer — or clears it back to unanswered.
 *
 * Always spread the whole result into `updateProfile`. It carries the legacy
 * boolean too, and writing the tri-state without it would leave the mirrored
 * `provides_leads` column contradicting the document.
 */
export function leadGenerationPatch(
  answer: LeadGenerationAnswer | null,
): { leadGenerationAnswer: LeadGenerationAnswer | ""; providesLeads: boolean | null } {
  return {
    leadGenerationAnswer: answer ?? "",
    // Unanswered is null, never false: save_org_profile_patch mirrors null
    // into provides_leads as NULL, which compute_fit_score does not compare.
    providesLeads: answer === null ? null : answer === "provided",
  };
}

/** The public chip's text. Never call this with `null`. */
export function leadGenerationLabel(answer: LeadGenerationAnswer): string {
  return answer === "provided" ? "Leads provided" : "Agents generate their own leads";
}
